import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import Select from "react-select";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { base_url } from "../../constants";

const BankQuestionsManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bankData = location?.state?.bankdata || {};
  const currentBankId = location?.state?.bank_id || bankData?.bank_id || location?.state?.course_id || "0";
  const bankName = bankData?.bank_name || (currentBankId !== "0" ? `Bank #${currentBankId}` : "Question Bank");

  document.title = `${bankName} - Questions | Medicotoon`;

  const localdata = localStorage.getItem("elmatary_admin");
  const adminData = localdata ? JSON.parse(localdata) : {};

  // Active Tab for adding / importing: 'new' | 'choose_pool' | 'import_unit'
  const [activeTab, setActiveTab] = useState("new");

  // Form states for adding new question
  const [questionText, setQuestionText] = useState("");
  const [questionImage, setQuestionImage] = useState("");
  const [explain, setExplain] = useState("");
  const [helpText, setHelpText] = useState("");
  const [helpPdf, setHelpPdf] = useState("");
  const [helpVideo, setHelpVideo] = useState("");
  const [answersList, setAnswersList] = useState([
    { id: 1, answer: "", answer_exp: "", checked: true },
    { id: 2, answer: "", answer_exp: "", checked: false },
  ]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bank Questions List & Pagination
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    limit: 10,
  });

  // Pool Questions States (Tab 2: Choose from Pool)
  const [poolQuestions, setPoolQuestions] = useState([]);
  const [loadingPool, setLoadingPool] = useState(false);
  const [poolSearch, setPoolSearch] = useState("");
  const [selectedPoolQuestionIds, setSelectedPoolQuestionIds] = useState([]);
  const [assigningFromPool, setAssigningFromPool] = useState(false);

  // Import from Course / Unit States (Tab 3: Import from Course / Unit)
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [importCourse, setImportCourse] = useState(null);
  const [importUnitsList, setImportUnitsList] = useState([]);
  const [loadingImportUnits, setLoadingImportUnits] = useState(false);
  const [importUnit, setImportUnit] = useState(null);
  const [importUnitQuestions, setImportUnitQuestions] = useState([]);
  const [loadingImportQuestions, setLoadingImportQuestions] = useState(false);
  const [selectedImportQuestionIds, setSelectedImportQuestionIds] = useState([]);
  const [importingFromUnit, setImportingFromUnit] = useState(false);

  // Edit Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editQuestionData, setEditQuestionData] = useState({});
  const [editAnswersList, setEditAnswersList] = useState([]);
  const [uploadingEditImg, setUploadingEditImg] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);

  // Selection & Bulk delete
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  // 1. Fetch Questions for this specific Bank
  const getBankQuestions = async (page = currentPage, limit = pageSize, search = searchTerm) => {
    setLoadingQuestions(true);
    try {
      const data_send = {
        bank_question_id: currentBankId,
        page: page,
        limit: limit,
        search: search,
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify(data_send)
      );

      if (res && res.message && Array.isArray(res.message)) {
        setQuestions(res.message);
        if (res.pagination) {
          setPaginationInfo(res.pagination);
        } else {
          setPaginationInfo({
            total_records: res.message.length,
            total_pages: Math.ceil(res.message.length / limit) || 1,
            current_page: page,
            limit: limit,
          });
        }
      } else {
        setQuestions([]);
        setPaginationInfo({
          total_records: 0,
          total_pages: 1,
          current_page: 1,
          limit: limit,
        });
      }
    } catch (err) {
      console.error("Error fetching bank questions", err);
      toast.error("Failed to load questions");
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // 2. Fetch Pool Questions
  const getPoolQuestions = async (search = poolSearch) => {
    setLoadingPool(true);
    try {
      const data_send = {
        unit_id: "0",
        course_id: "0",
        page: 1,
        limit: 200,
        search: search,
      };
      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify(data_send)
      );
      if (res && res.message && Array.isArray(res.message)) {
        setPoolQuestions(res.message);
      } else {
        setPoolQuestions([]);
      }
    } catch (err) {
      console.error("Error fetching pool questions", err);
      toast.error("Failed to load pool questions");
    } finally {
      setLoadingPool(false);
    }
  };

  // 3. Fetch Courses for Import
  const getCourses = async () => {
    setLoadingCourses(true);
    try {
      const data_send = {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
      };
      const res = await axios.post(
        `${base_url}/admin/courses/select_courses.php`,
        JSON.stringify(data_send)
      );
      if (res.status === "success" && Array.isArray(res.message)) {
        setCourses(
          res.message.map((c) => ({
            value: c.course_id,
            label: c.course_name,
            original: c,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching courses", err);
    } finally {
      setLoadingCourses(false);
    }
  };

  // 4. Fetch Units for Import Course
  const getImportUnits = async (courseId) => {
    if (!courseId) {
      setImportUnitsList([]);
      setImportUnit(null);
      setImportUnitQuestions([]);
      return;
    }
    setLoadingImportUnits(true);
    setImportUnitsList([]);
    setImportUnit(null);
    setImportUnitQuestions([]);
    setSelectedImportQuestionIds([]);
    try {
      const res = await axios.post(`${base_url}/admin/courses/select_course_units.php`, {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
        course_id: courseId,
      });
      if (res.status === "success" && Array.isArray(res.message)) {
        setImportUnitsList(
          res.message.map((u) => ({
            value: u.unit_id,
            label: u.unit_name,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching units", err);
    } finally {
      setLoadingImportUnits(false);
    }
  };

  // 5. Fetch questions from another Unit / Course for import
  const getImportUnitQuestions = async (unitId, courseId) => {
    if (!courseId) {
      setImportUnitQuestions([]);
      return;
    }
    setLoadingImportQuestions(true);
    setSelectedImportQuestionIds([]);
    try {
      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify({
          unit_id: unitId || "0",
          course_id: courseId,
          page: 1,
          limit: 300,
        })
      );
      if (res && res.message && Array.isArray(res.message)) {
        setImportUnitQuestions(res.message);
        setSelectedImportQuestionIds(res.message.map((q) => q.question_id));
      } else {
        setImportUnitQuestions([]);
      }
    } catch (err) {
      console.error("Error fetching unit questions", err);
    } finally {
      setLoadingImportQuestions(false);
    }
  };

  useEffect(() => {
    getBankQuestions(1, pageSize, searchTerm);
    getCourses();
  }, [currentBankId]);

  useEffect(() => {
    if (activeTab === "choose_pool") {
      getPoolQuestions(poolSearch);
    }
  }, [activeTab]);

  // Search debounce for bank questions
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      getBankQuestions(1, pageSize, searchTerm);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (paginationInfo.total_pages || 1)) {
      setCurrentPage(newPage);
      getBankQuestions(newPage, pageSize, searchTerm);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    getBankQuestions(1, newSize, searchTerm);
  };

  // Upload image
  const handleUploadImage = async (file, isEdit = false) => {
    if (!file) return;
    const formdata = new FormData();
    formdata.append("image", file);
    if (isEdit) setUploadingEditImg(true);
    else setUploadingImg(true);

    try {
      const res = await axios.post(
        `${base_url}/admin/image_uploader.php`,
        formdata
      );
      if (res) {
        if (isEdit) {
          setEditQuestionData((prev) => ({
            ...prev,
            question_image_url: res,
          }));
        } else {
          setQuestionImage(res);
        }
        toast.success("Image uploaded successfully");
      }
    } catch (err) {
      console.error("Error uploading image", err);
      toast.error("Error uploading image");
    } finally {
      if (isEdit) setUploadingEditImg(false);
      else setUploadingImg(false);
    }
  };

  // Answer options handlers
  const handleAnswerChange = (index, field, value) => {
    const list = [...answersList];
    list[index][field] = value;
    setAnswersList(list);
  };

  const handleSelectCorrect = (index) => {
    const list = answersList.map((item, idx) => ({
      ...item,
      checked: idx === index,
    }));
    setAnswersList(list);
  };

  const handleAddOption = () => {
    setAnswersList([
      ...answersList,
      {
        id: answersList.length + 1,
        answer: "",
        answer_exp: "",
        checked: false,
      },
    ]);
  };

  const handleRemoveOption = (index) => {
    if (answersList.length <= 2) {
      toast.warn("At least 2 answer options are required");
      return;
    }
    const list = answersList.filter((_, i) => i !== index);
    if (answersList[index].checked && list.length > 0) {
      list[0].checked = true;
    }
    setAnswersList(list);
  };

  // Create question directly for this bank (Tab 1)
  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      toast.warn("Please enter question title");
      return;
    }
    const validChecked = answersList.find((a) => a.checked);
    if (!validChecked || !validChecked.answer.trim()) {
      toast.warn("Please select and write the valid correct answer");
      return;
    }

    setIsSubmitting(true);
    try {
      const data_send = {
        bank_question_id: currentBankId,
        course_id: "0",
        unit_id: "0",
        question_text: questionText,
        question_answers: answersList
          ?.map((item) => `${item?.answer}/**exp**/${item?.answer_exp || ""}`)
          ?.join("//CAMP//"),
        question_valid_answer: validChecked.answer,
        question_image: questionImage || null,
        help_text: helpText || "",
        help_pdf: helpPdf || "",
        help_video: helpVideo || "",
        pdf_page: "",
        video_time: "",
        explain: explain || "",
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success("Question added successfully to this Bank");
        setQuestionText("");
        setQuestionImage("");
        setExplain("");
        setHelpText("");
        setHelpPdf("");
        setHelpVideo("");
        setAnswersList([
          { id: 1, answer: "", answer_exp: "", checked: true },
          { id: 2, answer: "", answer_exp: "", checked: false },
        ]);
        getBankQuestions(1, pageSize, searchTerm);
      } else {
        toast.error(res.message || "Failed to add question");
      }
    } catch (err) {
      console.error("Error creating question", err);
      toast.error("Server connection error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Import from Pool (Tab 2)
  const togglePoolQuestionSelection = (questionId) => {
    setSelectedPoolQuestionIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const toggleSelectAllPool = (availableQuestions) => {
    if (selectedPoolQuestionIds.length === availableQuestions.length && availableQuestions.length > 0) {
      setSelectedPoolQuestionIds([]);
    } else {
      setSelectedPoolQuestionIds(availableQuestions.map((q) => q.question_id));
    }
  };

  const handleAssignFromPool = async () => {
    if (selectedPoolQuestionIds.length === 0) {
      toast.warn("Please select at least one question");
      return;
    }

    setAssigningFromPool(true);
    try {
      const questionsToAssign = poolQuestions
        .filter((q) => selectedPoolQuestionIds.includes(q.question_id))
        .map((q) => {
          const validChecked = Array.isArray(q.question_answers)
            ? q.question_answers.find((a) => a.answer_check)
            : null;
          return {
            referenced_from: q.referenced_from || q.question_id,
            bank_question_id: currentBankId,
            course_id: "0",
            unit_id: "0",
            question_text: q.question_text,
            question_answers: Array.isArray(q.question_answers)
              ? q.question_answers
                .map((item) => `${item?.answer_text || item?.answer || ""}/**exp**/${item?.answer_exp || ""}`)
                .join("//CAMP//")
              : (q.question_answers || ""),
            question_valid_answer: validChecked
              ? (validChecked.answer_text || validChecked.answer || "")
              : (q.question_valid_answer || ""),
            question_image: q.question_image || null,
            help_text: q.help_text || "",
            help_pdf: q.help_pdf || "",
            help_video: q.help_video || "",
            pdf_page: q.pdf_page || "",
            video_time: q.video_time || "",
            explain: q.explain || "",
          };
        });

      const data_send = {
        bank_question_id: currentBankId,
        course_id: "0",
        unit_id: "0",
        questions: questionsToAssign,
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res && res.status === "success") {
        toast.success(`Successfully imported ${questionsToAssign.length} question(s) to this bank!`);
        setSelectedPoolQuestionIds([]);
        setCurrentPage(1);
        getBankQuestions(1, pageSize, searchTerm);
      } else {
        toast.error(res?.message || "Failed to import questions");
      }
    } catch (err) {
      console.error("Error assigning from pool", err);
      toast.error("An error occurred during assignment");
    } finally {
      setAssigningFromPool(false);
    }
  };

  // Import from Unit / Course (Tab 3)
  const toggleImportQuestionSelection = (qId) => {
    setSelectedImportQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  const toggleSelectAllImport = (availableQuestions) => {
    if (selectedImportQuestionIds.length === availableQuestions.length && availableQuestions.length > 0) {
      setSelectedImportQuestionIds([]);
    } else {
      setSelectedImportQuestionIds(availableQuestions.map((q) => q.question_id));
    }
  };

  const handleImportFromUnit = async () => {
    if (selectedImportQuestionIds.length === 0) {
      toast.warn("Please select at least one question to import");
      return;
    }

    setImportingFromUnit(true);
    try {
      const questionsToImport = importUnitQuestions
        .filter((q) => selectedImportQuestionIds.includes(q.question_id))
        .map((q) => {
          const validChecked = Array.isArray(q.question_answers)
            ? q.question_answers.find((a) => a.answer_check)
            : null;
          const trueRefId = q.referenced_from || q.question_id;

          return {
            referenced_from: trueRefId,
            bank_question_id: currentBankId,
            course_id: "0",
            unit_id: "0",
            question_text: q.question_text,
            question_answers: Array.isArray(q.question_answers)
              ? q.question_answers
                .map((item) => `${item?.answer_text || item?.answer || ""}/**exp**/${item?.answer_exp || ""}`)
                .join("//CAMP//")
              : (q.question_answers || ""),
            question_valid_answer: validChecked
              ? (validChecked.answer_text || validChecked.answer || "")
              : (q.question_valid_answer || ""),
            question_image: q.question_image || null,
            help_text: q.help_text || "",
            help_pdf: q.help_pdf || "",
            help_video: q.help_video || "",
            pdf_page: q.pdf_page || "",
            video_time: q.video_time || "",
            explain: q.explain || "",
          };
        });

      const data_send = {
        bank_question_id: currentBankId,
        course_id: "0",
        unit_id: "0",
        questions: questionsToImport,
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res && res.status === "success") {
        toast.success(`Successfully imported ${questionsToImport.length} question(s) to this bank!`);
        setSelectedImportQuestionIds([]);
        setCurrentPage(1);
        getBankQuestions(1, pageSize, searchTerm);
      } else {
        toast.error(res?.message || "Failed to import questions");
      }
    } catch (err) {
      console.error("Error importing questions from unit", err);
      toast.error("An error occurred during import");
    } finally {
      setImportingFromUnit(false);
    }
  };

  // Edit Question Handlers
  const handleOpenEdit = (item) => {
    setEditQuestionData({
      question_id: item.question_id,
      question_text: item.question_text || "",
      question_image_url: item.question_image || "",
      help_text: item.help_text || "",
      help_pdf: item.help_pdf || "",
      help_video: item.help_video || "",
      pdf_page: item.pdf_page || "",
      video_time: item.video_time || "",
      explain: item.explain || "",
    });

    if (Array.isArray(item.question_answers) && item.question_answers.length > 0) {
      setEditAnswersList(
        item.question_answers.map((ans, idx) => ({
          id: idx + 1,
          answer: ans.answer_text || ans.answer || "",
          answer_exp: ans.answer_exp || "",
          checked: !!ans.answer_check,
        }))
      );
    } else {
      setEditAnswersList([
        { id: 1, answer: "", answer_exp: "", checked: true },
        { id: 2, answer: "", answer_exp: "", checked: false },
      ]);
    }
    setIsEditModalOpen(true);
  };

  const handleEditAnswerChange = (index, field, value) => {
    const list = [...editAnswersList];
    list[index][field] = value;
    setEditAnswersList(list);
  };

  const handleEditSelectCorrect = (index) => {
    const list = editAnswersList.map((item, idx) => ({
      ...item,
      checked: idx === index,
    }));
    setEditAnswersList(list);
  };

  const handleEditAddOption = () => {
    setEditAnswersList([
      ...editAnswersList,
      {
        id: editAnswersList.length + 1,
        answer: "",
        answer_exp: "",
        checked: false,
      },
    ]);
  };

  const handleEditRemoveOption = (index) => {
    if (editAnswersList.length <= 2) {
      toast.warn("At least 2 answer options are required");
      return;
    }
    const list = editAnswersList.filter((_, i) => i !== index);
    if (editAnswersList[index].checked && list.length > 0) {
      list[0].checked = true;
    }
    setEditAnswersList(list);
  };

  const handleEditQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!editQuestionData.question_text.trim()) {
      toast.warn("Please enter question title");
      return;
    }
    const validChecked = editAnswersList.find((a) => a.checked);
    if (!validChecked || !validChecked.answer.trim()) {
      toast.warn("Please select and write the valid correct answer");
      return;
    }

    setSubmittingEdit(true);
    try {
      const data_send = {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
        question_id: editQuestionData.question_id,
        question_text: editQuestionData.question_text,
        question_answers: editAnswersList
          ?.map((item) => `${item?.answer}/**exp**/${item?.answer_exp || ""}`)
          ?.join("//CAMP//"),
        question_valid_answer: validChecked.answer,
        question_image: editQuestionData.question_image_url || "",
        help_text: editQuestionData.help_text || "",
        help_pdf: editQuestionData.help_pdf || "",
        help_video: editQuestionData.help_video || "",
        pdf_page: editQuestionData.pdf_page || "",
        video_time: editQuestionData.video_time || "",
        explain: editQuestionData.explain || "",
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/edit_ques.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success("Question updated successfully");
        setIsEditModalOpen(false);
        getBankQuestions(currentPage, pageSize, searchTerm);
      } else {
        toast.error(res.message || "Failed to update question");
      }
    } catch (err) {
      console.error("Error editing question", err);
      toast.error("Server connection error");
    } finally {
      setSubmittingEdit(false);
    }
  };

  // Delete Question Handler
  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await axios.post(`${base_url}/admin/Exams/delete_question.php`, {
          question_id: questionId,
        });
        toast.success("Question deleted successfully");
        setSelectedQuestionIds((prev) => prev.filter((id) => id !== questionId));
        getBankQuestions(currentPage, pageSize, searchTerm);
      } catch (err) {
        console.error("Error deleting question", err);
        toast.error("Failed to delete question");
      }
    }
  };

  // Toggle single question selection
  const toggleSelectQuestion = (qId) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  // Toggle select all on current page
  const toggleSelectAllPage = () => {
    if (selectedQuestionIds.length === questions.length && questions.length > 0) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(questions.map((q) => q.question_id));
    }
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    if (selectedQuestionIds.length === 0) {
      toast.warn("Please select at least one question to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedQuestionIds.length} question(s)?`
      )
    ) {
      setIsDeletingBulk(true);
      try {
        const promises = selectedQuestionIds.map((id) =>
          axios.post(`${base_url}/admin/Exams/delete_question.php`, {
            question_id: id,
          })
        );
        await Promise.all(promises);
        toast.success(`Successfully deleted ${selectedQuestionIds.length} question(s)`);
        setSelectedQuestionIds([]);
        getBankQuestions(currentPage, pageSize, searchTerm);
      } catch (err) {
        console.error("Error bulk deleting questions", err);
        toast.error("An error occurred during bulk deletion");
      } finally {
        setIsDeletingBulk(false);
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <Breadcrumbs
              title="Question Banks"
              breadcrumbItem={`${bankName} - Questions`}
            />
            <button
              className="btn btn-secondary d-flex align-items-center gap-1"
              onClick={() => navigate("/question-banks")}
            >
              <i className="bx bx-arrow-back font-size-16"></i> Back to Question Banks
            </button>
          </div>

          {/* Card: Add / Copy / Import Options */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm mb-4 border-top border-4 border-success">
                <div className="p-3 border-bottom bg-light bg-opacity-50">
                  <Nav pills className="nav-pills gap-2 flex-wrap">
                    <NavItem>
                      <NavLink
                        className={`cursor-pointer ${activeTab === "new" ? "active bg-success text-white fw-bold" : "bg-white border text-dark"}`}
                        onClick={() => setActiveTab("new")}
                      >
                        <i className="bx bx-plus-circle me-1 font-size-16"></i> Add New Question
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={`cursor-pointer ${activeTab === "choose_pool" ? "active bg-success text-white fw-bold" : "bg-white border text-dark"}`}
                        onClick={() => setActiveTab("choose_pool")}
                      >
                        <i className="bx bx-layer me-1 font-size-16"></i> Copy from Questions Pool
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={`cursor-pointer ${activeTab === "import_unit" ? "active bg-success text-white fw-bold" : "bg-white border text-dark"}`}
                        onClick={() => setActiveTab("import_unit")}
                      >
                        <i className="bx bx-import me-1 font-size-16"></i> Import from Course / Unit
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>

                <CardBody className="p-4">
                  {/* TAB 1: ADD NEW QUESTION */}
                  {activeTab === "new" && (
                    <form onSubmit={handleCreateQuestion}>
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Question Title <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Enter question text / title..."
                          value={questionText}
                          onChange={(e) => setQuestionText(e.target.value)}
                          required
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Question Image (Optional)
                        </label>
                        <div className="d-flex gap-2 align-items-center">
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleUploadImage(file, false);
                            }}
                          />
                          {uploadingImg && <Spinner size="sm" color="success" />}
                        </div>
                        {questionImage && (
                          <div className="mt-2 d-flex align-items-center gap-2">
                            <img
                              src={questionImage}
                              alt="preview"
                              style={{
                                maxHeight: "120px",
                                borderRadius: "6px",
                                border: "1px solid #ddd",
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setQuestionImage("")}
                            >
                              Remove Image
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Answer Options */}
                      <div className="mb-4 p-3 bg-light rounded border">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <label className="form-label fw-bold mb-0">
                              Answer Options (Select the correct answer) <span className="text-danger">*</span>
                            </label>
                            <small className="d-block text-muted">
                              Add choices and select which one is the correct answer.
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleAddOption}
                          >
                            <i className="bx bx-plus me-1"></i> Add Option
                          </button>
                        </div>

                        {answersList.map((item, index) => (
                          <div
                            key={index}
                            className="p-2 mb-2 bg-white rounded border d-flex flex-column gap-2"
                          >
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="radio"
                                name="correct_answer_bank_add"
                                checked={item.checked}
                                onChange={() => handleSelectCorrect(index)}
                                style={{
                                  cursor: "pointer",
                                  transform: "scale(1.2)",
                                }}
                                title="Mark as correct answer"
                              />
                              <Input
                                type="text"
                                placeholder={`Answer Option ${index + 1}`}
                                value={item.answer}
                                onChange={(e) =>
                                  handleAnswerChange(
                                    index,
                                    "answer",
                                    e.target.value
                                  )
                                }
                                required
                              />
                              {answersList.length > 2 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRemoveOption(index)}
                                >
                                  <i className="bx bx-trash"></i>
                                </button>
                              )}
                            </div>
                            <Input
                              type="text"
                              placeholder={`Explanation for answer ${index + 1} (Optional)`}
                              value={item.answer_exp || ""}
                              onChange={(e) =>
                                handleAnswerChange(
                                  index,
                                  "answer_exp",
                                  e.target.value
                                )
                              }
                              className="font-size-12 text-muted"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          General Explanation (Optional)
                        </label>
                        <textarea
                          className="form-control"
                          rows="2"
                          placeholder="Enter general explanation for the question / correct answer..."
                          value={explain}
                          onChange={(e) => setExplain(e.target.value)}
                        ></textarea>
                      </div>

                      <div className="d-flex justify-content-end">
                        <button
                          type="submit"
                          className="btn btn-success px-4 d-flex align-items-center gap-2 font-size-15"
                          disabled={isSubmitting || uploadingImg}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" />
                              <span>Adding Question...</span>
                            </>
                          ) : (
                            <>
                              <i className="bx bx-save font-size-18"></i>
                              <span>Save Question to Bank</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* TAB 2: COPY / CHOOSE FROM POOL */}
                  {activeTab === "choose_pool" && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div className="position-relative" style={{ minWidth: "300px" }}>
                          <Input
                            type="text"
                            placeholder="Search questions pool..."
                            value={poolSearch}
                            onChange={(e) => {
                              setPoolSearch(e.target.value);
                              getPoolQuestions(e.target.value);
                            }}
                          />
                          <i className="bx bx-search position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                        </div>
                        <button
                          type="button"
                          className="btn btn-success d-flex align-items-center gap-1 shadow-sm"
                          onClick={handleAssignFromPool}
                          disabled={selectedPoolQuestionIds.length === 0 || assigningFromPool}
                        >
                          {assigningFromPool ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="bx bx-import font-size-16"></i>
                          )}
                          <span>Copy Selected to Bank ({selectedPoolQuestionIds.length})</span>
                        </button>
                      </div>

                      {loadingPool ? (
                        <div className="text-center py-4">
                          <Spinner color="primary" />
                          <p className="mt-2 text-muted">Loading Questions Pool...</p>
                        </div>
                      ) : poolQuestions.length === 0 ? (
                        <div className="text-center py-4 text-muted border rounded p-3">
                          No pool questions found.
                        </div>
                      ) : (
                        <div className="table-responsive border rounded" style={{ maxHeight: "380px", overflowY: "auto" }}>
                          <table className="table table-hover align-middle table-sm mb-0">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th style={{ width: "40px" }} className="text-center">
                                  <input
                                    type="checkbox"
                                    className="form-check-input cursor-pointer"
                                    checked={
                                      poolQuestions.length > 0 &&
                                      selectedPoolQuestionIds.length === poolQuestions.length
                                    }
                                    onChange={() => toggleSelectAllPool(poolQuestions)}
                                  />
                                </th>
                                <th style={{ width: "80px" }}>ID</th>
                                <th>Question Text</th>
                                <th>Correct Answer</th>
                              </tr>
                            </thead>
                            <tbody>
                              {poolQuestions.map((q) => {
                                const isSelected = selectedPoolQuestionIds.includes(q.question_id);
                                return (
                                  <tr
                                    key={q.question_id}
                                    className={`cursor-pointer ${isSelected ? "table-active" : ""}`}
                                    onClick={() => togglePoolQuestionSelection(q.question_id)}
                                  >
                                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        className="form-check-input cursor-pointer"
                                        checked={isSelected}
                                        onChange={() => togglePoolQuestionSelection(q.question_id)}
                                      />
                                    </td>
                                    <td>#{q.question_id}</td>
                                    <td>
                                      <div style={{ maxWidth: "450px", wordBreak: "break-word" }}>
                                        {q.question_text}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="badge badge-soft-success">
                                        {q.question_valid_answer || "N/A"}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB 3: IMPORT FROM COURSE / UNIT */}
                  {activeTab === "import_unit" && (
                    <div>
                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <label className="form-label fw-bold">Select Source Course</label>
                          <Select
                            placeholder="Select Course..."
                            options={courses}
                            isLoading={loadingCourses}
                            value={importCourse}
                            onChange={(selected) => {
                              setImportCourse(selected);
                              if (selected) {
                                getImportUnits(selected.value);
                                getImportUnitQuestions("0", selected.value);
                              } else {
                                setImportUnitsList([]);
                                setImportUnit(null);
                                setImportUnitQuestions([]);
                              }
                            }}
                            isClearable
                          />
                        </Col>
                        <Col md={6}>
                          <label className="form-label fw-bold">Select Unit (Optional)</label>
                          <Select
                            placeholder="All Units / Select specific Unit..."
                            options={importUnitsList}
                            isLoading={loadingImportUnits}
                            value={importUnit}
                            isDisabled={!importCourse}
                            onChange={(selected) => {
                              setImportUnit(selected);
                              if (importCourse) {
                                getImportUnitQuestions(selected ? selected.value : "0", importCourse.value);
                              }
                            }}
                            isClearable
                          />
                        </Col>
                      </Row>

                      {importCourse && (
                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <span className="text-muted font-size-13">
                              Found <b>{importUnitQuestions.length}</b> questions in selected source
                            </span>
                            <button
                              type="button"
                              className="btn btn-success d-flex align-items-center gap-1 shadow-sm"
                              onClick={handleImportFromUnit}
                              disabled={selectedImportQuestionIds.length === 0 || importingFromUnit}
                            >
                              {importingFromUnit ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className="bx bx-import font-size-16"></i>
                              )}
                              <span>Import Selected to Bank ({selectedImportQuestionIds.length})</span>
                            </button>
                          </div>

                          {loadingImportQuestions ? (
                            <div className="text-center py-4">
                              <Spinner color="primary" />
                              <p className="mt-2 text-muted">Loading questions from unit...</p>
                            </div>
                          ) : importUnitQuestions.length === 0 ? (
                            <div className="text-center py-4 text-muted border rounded p-3">
                              No questions found in this course/unit.
                            </div>
                          ) : (
                            <div className="table-responsive border rounded" style={{ maxHeight: "380px", overflowY: "auto" }}>
                              <table className="table table-hover align-middle table-sm mb-0">
                                <thead className="table-light sticky-top">
                                  <tr>
                                    <th style={{ width: "40px" }} className="text-center">
                                      <input
                                        type="checkbox"
                                        className="form-check-input cursor-pointer"
                                        checked={
                                          importUnitQuestions.length > 0 &&
                                          selectedImportQuestionIds.length === importUnitQuestions.length
                                        }
                                        onChange={() => toggleSelectAllImport(importUnitQuestions)}
                                      />
                                    </th>
                                    <th style={{ width: "80px" }}>ID</th>
                                    <th>Question Text</th>
                                    <th>Correct Answer</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {importUnitQuestions.map((q) => {
                                    const isSelected = selectedImportQuestionIds.includes(q.question_id);
                                    return (
                                      <tr
                                        key={q.question_id}
                                        className={`cursor-pointer ${isSelected ? "table-active" : ""}`}
                                        onClick={() => toggleImportQuestionSelection(q.question_id)}
                                      >
                                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="checkbox"
                                            className="form-check-input cursor-pointer"
                                            checked={isSelected}
                                            onChange={() => toggleImportQuestionSelection(q.question_id)}
                                          />
                                        </td>
                                        <td>#{q.question_id}</td>
                                        <td>
                                          <div style={{ maxWidth: "450px", wordBreak: "break-word" }}>
                                            {q.question_text}
                                          </div>
                                        </td>
                                        <td>
                                          <span className="badge badge-soft-success">
                                            {q.question_valid_answer || "N/A"}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Current Bank Questions Table */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <div>
                      <h5 className="card-title font-size-16 mb-0 text-primary">
                        <i className="bx bx-list-ul me-1"></i> Questions in ({bankName})
                      </h5>
                      <small className="text-muted">
                        Total Questions: <b>{paginationInfo.total_records || questions.length}</b>
                      </small>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <div className="position-relative" style={{ minWidth: "280px" }}>
                        <Input
                          type="text"
                          className="form-control form-control-sm pe-4"
                          placeholder="Search questions by text or ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i
                          className="bx bx-search-alt position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#74788d",
                          }}
                        ></i>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => getBankQuestions(currentPage, pageSize, searchTerm)}
                        title="Refresh Questions"
                      >
                        <i className="bx bx-refresh me-1"></i> Refresh
                      </button>
                    </div>
                  </div>

                  {/* Top controls: Page Size Selector & Bulk Delete */}
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted font-size-13">Show:</span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-muted font-size-13">entries</span>
                    </div>

                    {/* Bulk Delete Button */}
                    {selectedQuestionIds.length > 0 && (
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-danger-subtle text-danger font-size-13 p-2">
                          {selectedQuestionIds.length} question(s) selected
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger d-flex align-items-center gap-1 shadow-sm"
                          onClick={handleBulkDelete}
                          disabled={isDeletingBulk}
                        >
                          {isDeletingBulk ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="bx bx-trash font-size-15"></i>
                          )}
                          Delete Selected ({selectedQuestionIds.length})
                        </button>
                      </div>
                    )}
                  </div>

                  {loadingQuestions ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading questions...</p>
                    </div>
                  ) : questions.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-folder-open font-size-40 mb-2 d-block text-muted"></i>
                      <p className="font-size-16 mb-1">
                        No questions in this bank yet.
                      </p>
                      <small>
                        Use the tabs above to add new questions, copy from pool, or import from another course/unit.
                      </small>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40px" }} className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input cursor-pointer"
                                  checked={
                                    questions.length > 0 &&
                                    selectedQuestionIds.length === questions.length
                                  }
                                  onChange={toggleSelectAllPage}
                                  title="Select/Deselect All on Page"
                                />
                              </th>
                              <th style={{ width: "50px" }}>#</th>
                              <th style={{ width: "120px" }}>Question ID</th>
                              <th>Question Title</th>
                              <th style={{ width: "100px" }}>Image</th>
                              <th>Answers</th>
                              <th style={{ width: "150px" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {questions.map((item, idx) => {
                              const rowNumber = (currentPage - 1) * pageSize + idx + 1;
                              const answers = item.question_answers || [];
                              const isSelected = selectedQuestionIds.includes(item.question_id);
                              return (
                                <tr
                                  key={item.question_id || idx}
                                  className={isSelected ? "table-active" : ""}
                                >
                                  <td className="text-center">
                                    <input
                                      type="checkbox"
                                      className="form-check-input cursor-pointer"
                                      checked={isSelected}
                                      onChange={() => toggleSelectQuestion(item.question_id)}
                                    />
                                  </td>
                                  <td><b>{rowNumber}</b></td>
                                  <td>{item.question_id}</td>
                                  <td>
                                    <div
                                      style={{
                                        maxWidth: "360px",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {item.question_text}
                                    </div>
                                  </td>
                                  <td>
                                    {item.question_image ? (
                                      <img
                                        src={item.question_image}
                                        alt="question"
                                        style={{
                                          width: "55px",
                                          height: "55px",
                                          objectFit: "cover",
                                          borderRadius: "6px",
                                          border: "1px solid #eee",
                                        }}
                                      />
                                    ) : (
                                      <span className="text-muted font-size-12">No Image</span>
                                    )}
                                  </td>
                                  <td>
                                    <ul
                                      style={{
                                        listStyleType: "none",
                                        padding: 0,
                                        margin: 0,
                                        textAlign: "left",
                                      }}
                                    >
                                      {answers.map((ans, aIdx) => (
                                        <li
                                          key={aIdx}
                                          style={{
                                            color: ans.answer_check ? "#28a745" : "#dc3545",
                                            fontWeight: ans.answer_check ? "600" : "normal",
                                            marginBottom: "3px",
                                          }}
                                        >
                                          {ans.answer_check ? "✓ " : "• "}
                                          {ans.answer_text}
                                        </li>
                                      ))}
                                    </ul>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleOpenEdit(item)}
                                        title="Edit Question"
                                      >
                                        <i className="bx bx-edit-alt me-1"></i> Edit
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDeleteQuestion(item.question_id)}
                                        title="Delete Question"
                                      >
                                        <i className="bx bx-trash me-1"></i> Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Server Pagination Controls */}
                      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted font-size-13">
                          Showing {(currentPage - 1) * pageSize + 1} to{" "}
                          {Math.min(currentPage * pageSize, paginationInfo.total_records || questions.length)}{" "}
                          of {paginationInfo.total_records || questions.length} entries
                        </div>

                        {paginationInfo.total_pages > 1 && (
                          <Pagination aria-label="Page navigation" size="sm" className="mb-0">
                            <PaginationItem disabled={currentPage <= 1}>
                              <PaginationLink
                                previous
                                onClick={() => handlePageChange(currentPage - 1)}
                              />
                            </PaginationItem>
                            {[...Array(paginationInfo.total_pages)].map((_, i) => {
                              const p = i + 1;
                              if (
                                p === 1 ||
                                p === paginationInfo.total_pages ||
                                (p >= currentPage - 2 && p <= currentPage + 2)
                              ) {
                                return (
                                  <PaginationItem key={p} active={p === currentPage}>
                                    <PaginationLink onClick={() => handlePageChange(p)}>
                                      {p}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            <PaginationItem disabled={currentPage >= paginationInfo.total_pages}>
                              <PaginationLink
                                next
                                onClick={() => handlePageChange(currentPage + 1)}
                              />
                            </PaginationItem>
                          </Pagination>
                        )}
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Edit Question Modal */}
      <Modal
        isOpen={isEditModalOpen}
        toggle={() => setIsEditModalOpen(!isEditModalOpen)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setIsEditModalOpen(!isEditModalOpen)}>
          <i className="bx bx-edit-alt me-1 text-primary"></i> Edit Question #{editQuestionData.question_id}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleEditQuestionSubmit}>
            <div className="mb-3">
              <label className="form-label fw-bold">
                Question Title <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows="3"
                value={editQuestionData.question_text || ""}
                onChange={(e) =>
                  setEditQuestionData({
                    ...editQuestionData,
                    question_text: e.target.value,
                  })
                }
                required
              ></textarea>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Question Image</label>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleUploadImage(file, true);
                  }}
                />
                {uploadingEditImg && <Spinner size="sm" color="primary" />}
              </div>
              {editQuestionData.question_image_url && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <img
                    src={editQuestionData.question_image_url}
                    alt="preview"
                    style={{
                      maxHeight: "100px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() =>
                      setEditQuestionData({
                        ...editQuestionData,
                        question_image_url: "",
                      })
                    }
                  >
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            {/* Answer Options */}
            <div className="mb-3 p-3 bg-light rounded border">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <label className="form-label fw-bold mb-0">
                  Answer Options <span className="text-danger">*</span>
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleEditAddOption}
                >
                  <i className="bx bx-plus me-1"></i> Add Option
                </button>
              </div>

              {editAnswersList.map((item, index) => (
                <div
                  key={index}
                  className="p-2 mb-2 bg-white rounded border d-flex flex-column gap-2"
                >
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="radio"
                      name="correct_answer_edit_bank"
                      checked={item.checked}
                      onChange={() => handleEditSelectCorrect(index)}
                      style={{ cursor: "pointer", transform: "scale(1.2)" }}
                    />
                    <Input
                      type="text"
                      placeholder={`Answer Option ${index + 1}`}
                      value={item.answer}
                      onChange={(e) =>
                        handleEditAnswerChange(index, "answer", e.target.value)
                      }
                      required
                    />
                    {editAnswersList.length > 2 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleEditRemoveOption(index)}
                      >
                        <i className="bx bx-trash"></i>
                      </button>
                    )}
                  </div>
                  <Input
                    type="text"
                    placeholder={`Explanation for answer ${index + 1} (Optional)`}
                    value={item.answer_exp || ""}
                    onChange={(e) =>
                      handleEditAnswerChange(index, "answer_exp", e.target.value)
                    }
                    className="font-size-12 text-muted"
                  />
                </div>
              ))}
            </div>

            {/* Explanation */}
            <div className="mb-3">
              <label className="form-label fw-bold">General Explanation</label>
              <textarea
                className="form-control"
                rows="2"
                value={editQuestionData.explain || ""}
                onChange={(e) =>
                  setEditQuestionData({
                    ...editQuestionData,
                    explain: e.target.value,
                  })
                }
              ></textarea>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submittingEdit || uploadingEditImg}
              >
                {submittingEdit ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default BankQuestionsManager;
